import gsap from "gsap";

import "../../css/modules/clock.css";

export class Clock {
	private _hour_hands: Array<HTMLElement> = [];
	private _minute_hands: Array<HTMLElement> = [];
	private _second_hands: Array<HTMLElement> = [];

	private _weekday_da_elements: Array<HTMLSpanElement> = [];
	private _day_da_elements: Array<HTMLSpanElement> = [];
	private _weekday_en_elements: Array<HTMLSpanElement> = [];
	private _day_en_elements: Array<HTMLSpanElement> = [];

	private _date_time_format_da: Intl.DateTimeFormat;
	private _date_time_format_en: Intl.DateTimeFormat;

	constructor() {

		const clock_elements: NodeListOf<HTMLElement> = document.querySelectorAll(
			".c-clock"
		);
		for (let i: number = 0; i < clock_elements.length; i++)
			this.setupClock(clock_elements[i]);

		setInterval(() => this.updateTime(), 1000);
		this.updateTime(false);

		const open_clock_elements: NodeListOf<HTMLElement> = document.querySelectorAll(
			".c-clock__activate"
		);
		for (let i: number = 0; i < open_clock_elements.length; i++) {
			open_clock_elements[i].addEventListener("click", (event: Event) =>
				this.handleFullscreenOpenClicked(event)
			);
		}

		const close_clock_element: HTMLElement = document.querySelector(
			".c-clock--fullscreen .c-page__close"
		);
		if (close_clock_element)
			close_clock_element.addEventListener("click", (event: Event) =>
				this.handleFullscreenCloseClicked(event)
			);
	}

	private setupClock(container: HTMLElement) {
		const clock_container: HTMLElement = container.querySelector(
			".c-clock__clock, .c-clock__toggle"
		);
		if (clock_container)
			clock_container.addEventListener("click", (event: Event) =>
				this.handleClockClicked(event)
			);

		const hour_hand: HTMLElement = container.querySelector(
			".c-clock__hand--hour"
		);
		if (hour_hand) this._hour_hands.push(hour_hand);

		const minute_hand: HTMLElement = container.querySelector(
			".c-clock__hand--minute"
		);
		if (minute_hand) this._minute_hands.push(minute_hand);

		const second_hand: HTMLElement = container.querySelector(
			".c-clock__hand--second"
		);
		if (second_hand) this._second_hands.push(second_hand);

		const weekday_da_element: HTMLSpanElement = container.querySelector(
			".c-clock__now--da .c-clock__weekday"
		);
		this._weekday_da_elements.push(weekday_da_element);
		const day_da_element: HTMLSpanElement = container.querySelector(
			".c-clock__now--da .c-clock__date"
		);
		this._day_da_elements.push(day_da_element);
		const weekday_en_element: HTMLSpanElement = container.querySelector(
			".c-clock__now--en .c-clock__weekday"
		);
		this._weekday_en_elements.push(weekday_en_element);
		const day_en_element: HTMLSpanElement = container.querySelector(
			".c-clock__now--en .c-clock__date"
		);
		this._day_en_elements.push(day_en_element);
	}

	private updateTime(animate: boolean = true) {
		const date = Date.now();
		const dateTimeFormat = new Intl.DateTimeFormat("da", {
			hour: "numeric",
			minute: "numeric",
			second: "numeric"
		});
		//@ts-ignore

		const [
			hourObj,
			,
			minuteObj,
			,
			secondObj
		] = dateTimeFormat.formatToParts(date);

		const { value: hour }: { value: any } = hourObj;
		const { value: minute }: { value: any } = minuteObj;
		const { value: second }: { value: any } = secondObj;

		const second_raw: number = (second / 60) * 360 - 90;
		const second_degrees: number = second_raw + minute * 360;

		const minute_raw: number = (minute / 60) * 360 - 90;
		const minute_degrees: number =
			minute_raw + hour * 360 + (360 / 60 / 60) * second;

		const hour_raw: number = ((hour % 12) / 12) * 360 - 90;
		const hour_degress: number = hour_raw + (360 / 12 / 60) * minute;

		if (animate) {
			gsap.to(this._hour_hands, {
				rotation: hour_degress,
				duration: 1,
				ease: "none"
			});
			gsap.to(this._minute_hands, {
				rotation: minute_degrees,
				duration: 1,
				ease: "none"
			});
			gsap.to(this._second_hands, {
				rotation: second_degrees,
				duration: 1,
				ease: "none"
			});
		} else {
			gsap.set(this._hour_hands, { rotation: hour_degress });
			gsap.set(this._minute_hands, { rotation: minute_degrees });
			gsap.set(this._second_hands, { rotation: second_degrees });
		}

		for (let i: number = 0; i < this._weekday_da_elements.length; i++) {
			if (
				this._weekday_da_elements[i] &&
				this._day_da_elements[i] &&
				this._weekday_en_elements[i] &&
				this._day_en_elements[i]
			) {
				const date_format_options: Object = {
					year: "numeric",
					month: "long",
					weekday: "long",
					day: "numeric",
					hour: "numeric",
					minute: "numeric",
					hour12: false
				};
				this._date_time_format_da = new Intl.DateTimeFormat(
					"da",
					date_format_options
				);
				this._date_time_format_en = new Intl.DateTimeFormat(
					"en-US",
					date_format_options
				);

				let [
					{ value: weekday },
					,
					{ value: day },
					,
					{ value: month },
					,
					{ value: year }
					//@ts-ignore
				] = this._date_time_format_da.formatToParts(date);

				this._weekday_da_elements[i].innerText = `${weekday}`;
				this._day_da_elements[i].innerText = `${day}. ${month} ${year}`;

				[
					{ value: weekday },
					,
					{ value: month },
					,
					{ value: day },
					,
					{ value: year }
					//@ts-ignore
				] = this._date_time_format_en.formatToParts(date);

				this._weekday_en_elements[i].innerText = `${weekday}`;
				this._day_en_elements[i].innerText = `${month} ${day}, ${year}`;
			}
		}
	}

	private handleClockClicked(event: Event) {
		event.preventDefault();
		document.body.classList.toggle("s-fullscreen-clock");
		return false;
	}

	private handleFullscreenOpenClicked(event: Event) {
		event.preventDefault();
		document.body.classList.add("s-fullscreen-clock");
		return false;
	}

	private handleFullscreenCloseClicked(event: Event) {
		event.preventDefault();
		document.body.classList.remove("s-fullscreen-clock");
		return false;
	}
}
